import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, ArrowRight, Bell, BellRing, CheckCheck, CheckCircle2, Info, Wrench, } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
// 30s is responsive enough for a notification badge while keeping the baseline
// request volume low — backgrounded tabs no longer poll at all (see below).
const POLL_MS = 30_000;
const SEVERITY_INFO_FALLBACK = { Icon: Info, color: 'text-sky-500' };
const severityIcon = {
    info: SEVERITY_INFO_FALLBACK,
    success: { Icon: CheckCircle2, color: 'text-emerald-500' },
    warning: { Icon: AlertTriangle, color: 'text-amber-500' },
    critical: { Icon: AlertTriangle, color: 'text-rose-500' },
};
function eventEmoji(event_type) {
    if (event_type.startsWith('ticket.assigned'))
        return Wrench;
    if (event_type.startsWith('ticket.created'))
        return BellRing;
    return Info;
}
export function NotificationBell() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const buttonRef = useRef(null);
    const prevCountRef = useRef(0);
    const countQ = useQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: () => api.get('/notifications/unread-count'),
        refetchInterval: POLL_MS,
        // Background polling ON (owner decision 2026-07-31): the job-chat bell
        // must fire even when StyxDesk sits in a background tab — an agent in
        // Excel must hear an engineer's message. unread-count is a feather-light
        // endpoint, so 30s background polling is cheap.
        refetchIntervalInBackground: true,
    });
    // ---- Job-chat bell (owner-approved 2026-07-31) --------------------------
    // When the unread count RISES and the newest notification is a chat
    // message, play a short bell + show a desktop notification + flash the tab
    // title — so chat messages are never missed while the app is open anywhere.
    const flashTimer = useRef(null);
    const bellPrevRef = useRef(0);
    useEffect(() => {
        const unread = countQ.data?.unread ?? 0;
        const prev = bellPrevRef.current;
        bellPrevRef.current = unread;
        if (unread <= prev || prev === 0)
            return; // first load / no increase
        void (async () => {
            try {
                const page = await api.get('/notifications?limit=1');
                const latest = page.items?.[0];
                if (!latest || !String(latest.event_type ?? '').includes('chat'))
                    return;
                // 1. Bell — WebAudio two-tone ding (no asset; ignored if the browser
                //    blocks audio before a user gesture).
                try {
                    const Ctx = window.AudioContext ?? window.webkitAudioContext;
                    if (Ctx) {
                        const ctx = new Ctx();
                        [880, 660].forEach((freq, i) => {
                            const osc = ctx.createOscillator();
                            const gain = ctx.createGain();
                            osc.frequency.value = freq;
                            osc.connect(gain);
                            gain.connect(ctx.destination);
                            gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.18);
                            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.3);
                            osc.start(ctx.currentTime + i * 0.18);
                            osc.stop(ctx.currentTime + i * 0.18 + 0.32);
                        });
                        window.setTimeout(() => void ctx.close(), 1200);
                    }
                }
                catch {
                    /* audio blocked — desktop notification still fires */
                }
                // 2. Desktop notification (permission asked once, lazily).
                try {
                    if ('Notification' in window) {
                        if (Notification.permission === 'default')
                            await Notification.requestPermission();
                        if (Notification.permission === 'granted') {
                            const n = new Notification(latest.title || 'New job-chat message', {
                                body: latest.body || '',
                                tag: 'styx-chat',
                            });
                            n.onclick = () => {
                                window.focus();
                                if (latest.link_url)
                                    navigate(latest.link_url);
                                n.close();
                            };
                        }
                    }
                }
                catch {
                    /* best-effort */
                }
                // 3. Flash the tab title for ~12s.
                if (flashTimer.current)
                    window.clearInterval(flashTimer.current);
                const original = document.title;
                let onTick = false;
                flashTimer.current = window.setInterval(() => {
                    onTick = !onTick;
                    document.title = onTick ? '💬 New chat message' : original;
                }, 1000);
                window.setTimeout(() => {
                    if (flashTimer.current)
                        window.clearInterval(flashTimer.current);
                    flashTimer.current = null;
                    document.title = original;
                }, 12000);
            }
            catch {
                /* never break the bell UI on a fetch hiccup */
            }
        })();
    }, [countQ.data, navigate]);
    const listQ = useQuery({
        queryKey: ['notifications', 'list'],
        queryFn: () => api.get('/notifications?limit=20'),
        enabled: open,
        refetchInterval: open ? POLL_MS : false,
    });
    const items = listQ.data?.items ?? [];
    const markRead = useMutation({
        mutationFn: (id) => api.post(`/notifications/${id}/read`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
    const markAll = useMutation({
        mutationFn: () => api.post('/notifications/read-all', {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
    // Visual cue when a new alert arrives between polls.
    const unread = countQ.data?.unread ?? 0;
    const justArrived = unread > prevCountRef.current;
    useEffect(() => {
        prevCountRef.current = unread;
    }, [unread]);
    // Close on outside click.
    useEffect(() => {
        if (!open)
            return;
        function onDoc(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        window.addEventListener('mousedown', onDoc);
        return () => window.removeEventListener('mousedown', onDoc);
    }, [open]);
    const onItemClick = (n) => {
        if (!n.is_read)
            markRead.mutate(n.id);
        if (n.link_url) {
            const url = new URL(n.link_url, window.location.origin);
            navigate(url.pathname + url.search + url.hash);
            setOpen(false);
        }
    };
    return (_jsxs("div", { className: "relative", children: [_jsxs("button", { ref: buttonRef, onClick: () => setOpen((o) => !o), className: cn('relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-100', open && 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'), "aria-label": `Notifications (${unread} unread)`, children: [unread > 0 ? (_jsx(BellRing, { className: cn('h-5 w-5', justArrived && 'animate-pulse text-brand-600') })) : (_jsx(Bell, { className: "h-5 w-5" })), unread > 0 && (_jsx("span", { className: "absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow-sm", children: unread > 99 ? '99+' : unread }))] }), open && (_jsxs("div", { ref: popoverRef, className: "absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(BellRing, { className: "h-4 w-4 text-brand-500" }), _jsx("span", { className: "text-sm font-semibold", children: "Notifications" }), unread > 0 && (_jsxs("span", { className: "rounded-full bg-rose-100 px-1.5 py-0.5 text-tag text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: [unread, " new"] }))] }), items.length > 0 && (_jsxs("button", { onClick: () => markAll.mutate(), disabled: markAll.isPending || unread === 0, className: "flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-40", children: [_jsx(CheckCheck, { size: 12 }), "Mark all read"] }))] }), _jsxs("div", { className: "max-h-96 overflow-y-auto", children: [listQ.isLoading && (_jsx("div", { className: "px-4 py-8 text-center text-xs text-slate-500", children: "Loading\u2026" })), !listQ.isLoading && items.length === 0 && (_jsxs("div", { className: "px-4 py-10 text-center", children: [_jsx(Bell, { className: "mx-auto h-8 w-8 text-slate-300" }), _jsx("div", { className: "mt-2 text-sm text-slate-500", children: "You're all caught up." })] })), items.map((n) => {
                                const sev = severityIcon[n.severity] ?? SEVERITY_INFO_FALLBACK;
                                const EvIcon = eventEmoji(n.event_type);
                                const Icon = n.severity === 'info' ? EvIcon : sev.Icon;
                                return (_jsxs("button", { onClick: () => onItemClick(n), className: cn('flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50', !n.is_read && 'bg-brand-50/40 dark:bg-brand-950/20'), children: [_jsx("div", { className: cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800', sev.color), children: _jsx(Icon, { className: "h-3.5 w-3.5" }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: cn('truncate text-sm', n.is_read ? 'text-slate-600 dark:text-slate-400' : 'font-semibold text-slate-900 dark:text-slate-100'), children: n.title }), !n.is_read && _jsx("span", { className: "ml-auto h-2 w-2 shrink-0 rounded-full bg-brand-500" })] }), n.body && (_jsx("div", { className: "mt-0.5 line-clamp-2 text-xs text-slate-500", children: n.body })), _jsx("div", { className: "mt-1 text-[10px] uppercase tracking-wide text-slate-400", children: formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) })] })] }, n.id));
                            })] }), _jsx("div", { className: "border-t border-slate-200 dark:border-slate-800", children: _jsxs(Link, { to: "/notifications", onClick: () => setOpen(false), className: "flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-medium text-brand-600 hover:bg-slate-50 dark:hover:bg-slate-800/60", children: ["View all notifications", _jsx(ArrowRight, { size: 12 })] }) })] }))] }));
}
